import { Form, NavLink, useMatch } from 'react-router';
import { T, useTX } from '@transifex/react';
import {
  ChevronRight,
  ExternalLink,
  Globe,
  HelpCircle,
  LogOut,
  SlidersHorizontal,
  User,
} from 'lucide-react';
import { useUser } from '~/hooks/user';
import { ampli } from '~/third-party/ampli/.client';

import { DropdownMenuItem, DropdownMenuLabel } from '~/components/ui/dropdown-menu';
import { $path } from 'safe-routes';
import { getDisplayName } from '~/lib/utils';
import { defaultLanguage, LANGUAGES } from '~/lib/languages';
import { GamificationPills } from '~/components/ui/gamification/gamification-metrics';

const ZENDESK_SUPPORT_URL = {
  en: 'https://fujitsulaunchpad.zendesk.com/hc/en-am/categories/54710431965593-Startable',
  ja: 'https://fujitsulaunchpad.zendesk.com/hc/ja/categories/54710431965593-Startable',
};

type NavbarUserMenuProps = {
  onSwitchToLanguage: () => void;
};

export function NavbarUserMenu({ onSwitchToLanguage }: NavbarUserMenuProps) {
  const user = useUser();
  const tx = useTX();
  const locale = tx.getCurrentLocale();
  const isSignupPage = useMatch('/signup');
  const isLoginPage = useMatch('/login');
  const isVerifyPage = useMatch('/signup/verify');
  const supportUrl = locale === 'ja' ? ZENDESK_SUPPORT_URL.ja : ZENDESK_SUPPORT_URL.en;

  if (isSignupPage || isLoginPage || isVerifyPage || !user) {
    return null;
  }

  return (
    <>
      <DropdownMenuLabel className="flex flex-col">
        <p className="text-body-big-bold text-text-900">{getDisplayName(user)}</p>
        <p className="text-body-small text-text-700">{user.email}</p>
        <GamificationPills
          className="
            mt-sm
            md:hidden
          "
        />
      </DropdownMenuLabel>
      {user.accountType === 'LEARNER' ? (
        <>
          <DropdownMenuItem asChild>
            <NavLink
              to={$path('/learners/:learnerId', { learnerId: user.id })}
              className="w-full text-left"
              onClick={() => ampli.clickMyPortfolioUserMenu()}
            >
              <User className="size-5" />
              <span className="text-body-big">
                <T _str="Profile & Portfolio" />
              </span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <NavLink to={$path('/settings')} className="w-full text-left">
              <SlidersHorizontal className="size-5" />
              <span className="text-body-big">
                <T _str="Settings" />
              </span>
            </NavLink>
          </DropdownMenuItem>
        </>
      ) : (
        <DropdownMenuItem asChild>
          <NavLink to={$path('/onboarding')} className="w-full text-left">
            <User className="size-5" />
            <span className="text-body-big">
              <T _str="Profile" />
            </span>
          </NavLink>
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        onSelect={e => {
          e.preventDefault();
          onSwitchToLanguage();
        }}
        className="justify-between"
      >
        <div className="flex items-center gap-2">
          <Globe className="size-5" />
          <span className="text-body-big">
            {LANGUAGES.find(l => l.code === locale)?.label || defaultLanguage.label}
          </span>
        </div>
        <ChevronRight className="size-4 text-muted-foreground" />
      </DropdownMenuItem>

      {user.accountType === 'LEARNER' && (
        <DropdownMenuItem asChild>
          <a href={supportUrl} target="_blank" rel="noopener noreferrer">
            <HelpCircle className="size-5 shrink-0" />
            <span className="flex-1 text-body-big">
              <T _str="Support" />
            </span>
            <ExternalLink className="size-4 shrink-0" />
          </a>
        </DropdownMenuItem>
      )}

      <Form method="post" action={$path('/logout')} onSubmit={() => ampli.clickLogOutUserMenu()}>
        <DropdownMenuItem asChild variant="destructive">
          <button type="submit" className="flex w-full items-center gap-2 text-left">
            <LogOut className="size-5" />
            <span className="text-body-big">
              <T _str="Log Out" />
            </span>
          </button>
        </DropdownMenuItem>
      </Form>
    </>
  );
}
